import { inject, Injectable } from '@angular/core';
import { FilterDepartmentService } from '@dep/filters/services';
import { ApiRouteWorkShiftService } from '../../../api/elite/routes/work-shift.service';
import { Observable } from 'rxjs';
import { WorkShift } from '../../../api/elite/types/work-shift';
import { ScrapReason } from '../../../api/elite/types/scrap-reason';
import { ApiRouteScrapService } from '../../../api/elite/routes/scrap.service';
import { Department } from '../../../api/elite/types/department';
import { ApiRouteDepartmentService } from '../../../api/elite/routes/department.service';
import { DepartmentApiDto } from '../../../api/elite/dto/department.dto';
import { shareReplay, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class FilterDepartmentApiService {
  private readonly departmentService = inject(FilterDepartmentService);

  private readonly apiShiftRouter = inject(ApiRouteWorkShiftService);
  private readonly apiScrapRouter = inject(ApiRouteScrapService);
  private readonly departmentApiRouter = inject(ApiRouteDepartmentService);

  shifts(): Observable<WorkShift[]> {
    return this.apiShiftRouter
      .all(this.departmentService.NAME)
      .pipe(shareReplay({ bufferSize: 1, refCount: false }));
  }

  scrapReasons(): Observable<ScrapReason[]> {
    return this.apiScrapRouter
      .getByDepartment(this.departmentService.NAME)
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));
  }

  getDepartment(): Observable<Department> {
    return this.departmentApiRouter
      .getOneByName(this.departmentService.NAME)
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));
  }

  /**
   * Shifts linked to this department, resolved by department id (looked up from
   * the name at runtime). Uses the reliable /department/:id/shifts route rather
   * than the by-name ?department= endpoint. Used by the start-up conditions
   * checklist's Turno selector.
   */
  shiftsByDepartment(): Observable<WorkShift[]> {
    return this.getDepartment().pipe(
      switchMap((dep) => this.departmentApiRouter.getShifts(dep.id))
    );
  }

  getDataV2(
    data: DepartmentApiDto['dataV2']['params'] = {}
  ): Observable<Department> {
    return this.departmentApiRouter.getDataV2(
      this.departmentService.NAME,
      data
    );
  }
}
